import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';

export default function ClinicianDashboard() {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [intake, setIntake] = useState(null);
  const [note, setNote] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    apiClient.get('/appointments/mine').then((res) => setAppointments(res.data.appointments)).finally(() => setLoading(false));
  }, []);

  async function openAppointment(appt) {
    setSelected(appt);
    setIntake(null);
    setNote(null);
    setMessage('');
    try {
      const res = await apiClient.get(`/ai-intake/${appt.id}`);
      setIntake(res.data.intake);
    } catch {
      setIntake(null);
    }
    try {
      const res = await apiClient.get(`/clinical-notes/${appt.id}`);
      setNote(res.data.note);
      setNoteDraft(res.data.note.finalContent || '');
    } catch {
      setNote(null);
    }
  }

  async function confirmAppointment() {
    const res = await apiClient.put(`/appointments/${selected.id}/confirm`, {});
    setSelected(res.data.appointment);
    setMessage('Appointment confirmed and patient notified.');
  }

  async function saveNote(approve) {
    const res = await apiClient.put(`/clinical-notes/${selected.id}`, {
      finalContent: noteDraft,
      isApproved: approve,
    });
    setNote(res.data.note);
    setMessage(approve ? 'Note approved.' : 'Draft saved.');
  }

  async function shareNote() {
    const res = await apiClient.put(`/clinical-notes/${selected.id}`, { sharedWithPatient: true });
    setNote(res.data.note);
    setMessage('Note shared with patient.');
  }

  return (
    <div className="dashboard-page">
      <div className="container">
        <div className="dashboard-header">
          <div>
            <span className="eyebrow">Clinician Dashboard</span>
            <h1>Welcome, {user?.fullName}</h1>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '0.9fr 1.4fr', gap: 24 }}>
          <div>
            <h3>My Schedule</h3>
            {loading ? <p>Loading…</p> : (
              <div className="appt-list">
                {appointments.map((a) => (
                  <div key={a.id} className={`card appt-card`} style={{ cursor: 'pointer', borderColor: selected?.id === a.id ? 'var(--color-primary)' : undefined }} onClick={() => openAppointment(a)}>
                    <div>
                      <h4>{a.patient?.fullName}</h4>
                      <p className="appt-meta">{new Date(a.scheduledAt).toLocaleString()}</p>
                    </div>
                    <span className={`status-pill status-${a.status}`}>{a.status}</span>
                  </div>
                ))}
                {!appointments.length && <div className="card empty-state">No appointments assigned yet.</div>}
              </div>
            )}
          </div>

          <div>
            {!selected ? (
              <div className="card empty-state">Select an appointment to review intake and notes.</div>
            ) : (
              <div className="card" style={{ padding: 24 }}>
                <h3>{selected.patient?.fullName}</h3>
                <p className="appt-meta">{new Date(selected.scheduledAt).toLocaleString()} · {selected.appointmentType}</p>
                {message && <div className="alert alert-success">{message}</div>}

                {selected.status === 'requested' && (
                  <button className="btn btn-secondary" onClick={confirmAppointment}>Confirm Appointment</button>
                )}
                {selected.status === 'confirmed' && (
                  <Link to={`/session/${selected.id}`} className="btn btn-primary">Join Session</Link>
                )}

                <h4 style={{ marginTop: 24 }}>AI Intake Summary (review before session)</h4>
                {intake ? (
                  <div className="form-field">
                    <textarea readOnly rows={6} value={intake.aiDraftSummary || 'Patient has not finalized intake yet.'} />
                  </div>
                ) : <p className="appt-meta">No intake started yet.</p>}

                <h4 style={{ marginTop: 24 }}>Session Note</h4>
                <div className="form-field">
                  <textarea rows={8} value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} placeholder="Write or edit the clinical note here. AI drafts, if generated, are a starting point only — you are responsible for the final content." />
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button className="btn btn-outline" style={{ color: 'var(--color-primary)', borderColor: 'var(--color-primary)' }} onClick={() => saveNote(false)}>Save Draft</button>
                  <button className="btn btn-secondary" onClick={() => saveNote(true)}>Approve Note</button>
                  {note?.isApproved && !note?.sharedWithPatient && (
                    <button className="btn btn-primary" onClick={shareNote}>Share with Patient</button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
